import {
  isValidThemeMode,
  type ThemeContextValue,
  type ThemeMode,
  useThemeEffect,
} from '@kyumo/shared-ui';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'theme-mode';
const DEFAULT_MODE: ThemeMode = 'system';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isValidThemeMode(stored)) return stored;
  } catch {
    // localStorage アクセス失敗時はデフォルト
  }
  return DEFAULT_MODE;
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getInitialMode);
  const { resolvedTheme } = useThemeEffect({ mode: themeMode });

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage アクセス失敗時はメモリ内のみで動作
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ themeMode, resolvedTheme, setThemeMode }),
    [themeMode, resolvedTheme, setThemeMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * 描画中のテーマ（light / dark）だけを参照する。
 * Provider 外（単体テスト等）でも例外を投げず light を返すため、
 * 表示コンポーネントの配色切り替えに使う。
 */
export function useResolvedTheme(): ThemeContextValue['resolvedTheme'] {
  return useContext(ThemeContext)?.resolvedTheme ?? 'light';
}
